import { motion } from "framer-motion";
import { Building2, MapPin, Fingerprint, Star } from "lucide-react";

export const NAICS = [
  ["541614", "Process, Physical Distribution, and Logistics Consulting Services", true],
  ["423110", "Automobile and Other Motor Vehicle Merchant Wholesalers", false],
  ["423120", "Motor Vehicle Supplies and New Parts Merchant Wholesalers", false],
  ["423430", "Computer and Computer Peripheral Equipment and Software Merchant Wholesalers", false],
  ["423910", "Sporting and Recreational Goods and Supplies Merchant Wholesalers", false],
  ["424710", "Petroleum Bulk Stations and Terminals", false],
  ["424720", "Petroleum and Petroleum Products Merchant Wholesalers (except Bulk Stations and Terminals)", false],
];

const CREDENTIALS = [
  { label: "Unique Entity ID (UEI)", value: "FJNKCGJV1QQ6", note: "SAM.gov · Active", testid: "credential-uei" },
  { label: "CAGE / NCAGE Code", value: "01GUU", note: "NATO Codification", testid: "credential-ncage" },
  { label: "Legal Business Name", value: "DEFCOIN LTD", note: "Foreign Entity · EU", testid: "credential-legal-name" },
  { label: "UIC / VAT ID", value: ["208073340", "BG208073340"], note: "Bulgaria Commercial Register", testid: "credential-uic-vat" },
];

const CORPORATE = [
  {
    icon: Building2,
    label: "Business Structure",
    value: "DEFCOIN Ltd. / Foreign Entity / Private Limited Company / Small Business / EU-Based",
    testid: "corporate-structure",
  },
  {
    icon: MapPin,
    label: "Strategic Location",
    value: "Bulgaria (EU / NATO Eastern Flank — proximity to Novo Selo & Graf Ignatievo operations)",
    testid: "corporate-location",
  },
  {
    icon: Fingerprint,
    label: "Registrations",
    value: "SAM.gov Registered Foreign Vendor · UEI FJNKCGJV1QQ6 · NATO NCAGE 01GUU · UIC 208073340 · VAT ID BG208073340",
    testid: "corporate-registrations",
  },
];

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.6, delay },
});

export default function Compliance() {
  return (
    <section id="compliance" className="py-16 md:py-24 lg:py-28 bg-[#14171A]/40 border-y border-slate-800" data-testid="compliance-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div {...fade()} className="mb-8 md:mb-12">
          <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-amber-600 mb-3 flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-amber-600" /> 02 / Government Credentials &amp; NAICS
          </p>
          <h2 className="font-display font-bold uppercase tracking-tight text-xl sm:text-2xl text-slate-50">
            Contracting-Officer Data Matrix
          </h2>
          <p className="mt-3 text-slate-400 max-w-2xl text-sm">
            Official SAM.gov and NATO registration identifiers, structured for rapid KO review and past-performance lookups.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-800 border border-slate-800 mb-6 lg:mb-8" data-testid="credential-badges">
          {CREDENTIALS.map((c, i) => (
            <motion.div key={c.label} {...fade(i * 0.08)} className="bg-[#0D0F12] p-4 sm:p-5 flex flex-col gap-2" data-testid={c.testid}>
              <p className="font-mono text-[9px] sm:text-[10px] tracking-[0.2em] uppercase text-slate-500 leading-snug">{c.label}</p>
              <p className="font-mono text-sm sm:text-base lg:text-lg tracking-[0.08em] text-[#C9A24A] leading-snug" data-testid={`${c.testid}-value`}>
                {Array.isArray(c.value) ? c.value.map((v) => <span key={v} className="block">{v}</span>) : c.value}
              </p>
              <p className="font-mono text-[9px] tracking-[0.15em] uppercase text-slate-600 mt-auto">{c.note}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid lg:grid-cols-5 gap-6 lg:gap-8">
          <motion.div {...fade(0.1)} className="lg:col-span-3 border border-slate-800 bg-[#0D0F12]" data-testid="naics-matrix">
            <div className="px-4 sm:px-6 py-3.5 border-b border-slate-800 flex items-center justify-between gap-3">
              <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-slate-400">NAICS Codes — SAM.gov Registration</span>
              <span className="font-mono text-[11px] tracking-[0.25em] text-blue-500 shrink-0">{NAICS.length} ACTIVE</span>
            </div>
            {NAICS.map(([code, desc, primary], i) => (
              <motion.div
                key={code}
                initial={{ opacity: 0, x: -14 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className={`group flex items-start gap-4 sm:gap-6 px-4 sm:px-6 py-3.5 border-b border-slate-800/70 last:border-b-0 transition-colors duration-200 ${
                  primary ? "bg-amber-600/[0.07] border-l-2 border-l-amber-600" : "hover:bg-slate-900/60"
                }`}
                data-testid={`naics-row-${code}`}
              >
                <span className={`font-mono text-sm tracking-[0.15em] w-16 shrink-0 ${primary ? "text-amber-500" : "text-amber-600"}`}>{code}</span>
                <div className="flex-1 min-w-0">
                  <span className={`text-sm transition-colors ${primary ? "text-slate-50" : "text-slate-300 group-hover:text-slate-50"}`}>{desc}</span>
                  {primary && (
                    <span className="ml-2 inline-flex items-center gap-1 align-middle font-mono text-[9px] tracking-[0.2em] uppercase text-[#0D0F12] bg-amber-500 px-1.5 py-0.5" data-testid="naics-primary-badge">
                      <Star className="w-2.5 h-2.5 fill-current" /> Primary
                    </span>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>

          <div className="lg:col-span-2 space-y-px bg-slate-800 border border-slate-800 h-fit" data-testid="corporate-data">
            {CORPORATE.map((item, i) => (
              <motion.div key={item.label} {...fade(i * 0.1)} className="bg-[#14171A] p-5 sm:p-6 group hover:bg-[#171B20] transition-colors duration-300" data-testid={item.testid}>
                <div className="flex items-center gap-3 mb-2.5">
                  <item.icon className="w-4 h-4 text-blue-500 group-hover:text-amber-600 transition-colors duration-300" />
                  <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">{item.label}</span>
                </div>
                <p className="text-sm text-slate-300 leading-relaxed">{item.value}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
